/* ============================================================================
   EVA KARPATI — GLOBAL REVEAL
   assets/ek-reveal.js  ·  site-wide rise-on-scroll
   ----------------------------------------------------------------------------
   Applies the rise reveal to every section on the site without editing a single
   section file. Pairs with assets/ek-reveal.css.

   INSTALLATION — three edits, once.

   1. layout/theme.liquid, immediately after the opening <head> tag. This must
      be inline and must run before paint, or elements flash visible before the
      hidden state applies:

        <script>document.documentElement.classList.add('ek-js');</script>

   2. layout/theme.liquid, in <head> with the other stylesheets:

        {{ 'ek-reveal.css' | asset_url | stylesheet_tag }}

   3. layout/theme.liquid, before </body>:

        <script src="{{ 'ek-reveal.js' | asset_url }}" defer></script>

   Then replace the .ek-reveal block in ek-custom.css with the patch at the
   bottom of ek-reveal.css — that rule currently hides content with no JS gate.

   HOW TARGETS ARE CHOSEN, per section:
     • A section containing .ek-reveal elements keeps its existing look — those
       elements are the targets, with no stagger and no auto-targeting on top.
       They are observed here as well as by ek-custom.js, which only scans once
       on DOMContentLoaded and so misses editor-loaded sections.
     • A section that animates itself (ek-timeline and anything else carrying
       data-ek-reveal="self") is left alone entirely.
     • Otherwise the script walks down through single-child wrappers to the
       first element with real content, and reveals that element's children in
       sequence. That is the row of cards, the heading-and-body pair, the
       columns — the things a reader perceives as arriving.

   BACKGROUND SURFACES ARE NEVER FADED. An element that fills its parent and
   carries the paint is a wrapper, not content — fading it punches a hole in
   the page and whatever sits behind shows through. This theme's hero is
   position:sticky, so a section fading in over it would briefly reveal the
   pinned hero underneath. The script steps inside such an element and animates
   its contents instead; a surface with nothing inside is pure decoration and
   is left alone entirely.

   OPTING OUT:
     • Add class ek-no-reveal to any element or section to skip it.
     • Add data-ek-reveal="off" to a section to skip the whole section.
     • Section ids matching SKIP_SECTIONS below are skipped automatically.

   Fixed and sticky elements are detected and never animated, so headers,
   sticky bars, drawers, cookie banners and modals behave normally.

   Idempotent, safe to load twice, handles theme-editor section loads, and
   disables itself under prefers-reduced-motion.
   ============================================================================ */
(function () {
  if (window.__ekRevealInit) return;
  window.__ekRevealInit = true;

  /* The first section on the page is usually the hero, and the hero usually
     holds the largest-contentful-paint element. Revealing it means that
     element starts at opacity 0, so LCP is not recorded until the fade
     finishes — typically several hundred milliseconds of measured delay on
     a metric Google watches. Set this to true to leave the first section
     alone; everything below the fold still animates and no visitor sees a
     difference, because the hero is on screen at load anyway. */
  var SKIP_FIRST_SECTION = false;

  /* Sections never animated — matched against the shopify-section id. */
  var SKIP_SECTIONS = [
    'header', 'footer', 'announcement', 'cart', 'drawer',
    'popup', 'modal', 'newsletter-popup', 'age-gate'
  ];

  /* How deep to walk through single-child wrappers before giving up. */
  var MAX_DEPTH = 6;

  /* Beyond this many siblings, reveal the container as one piece instead of
     staggering — a 40-item grid staggered item by item takes far too long. */
  var MAX_SIBLINGS = 14;

  /* Cap the stagger index so the last item in a long row is not left waiting. */
  var MAX_STAGGER_STEPS = 8;

  var reduce = window.matchMedia &&
               window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var io = null;

  function observer() {
    if (io) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        el.classList.add('is-in');
        io.unobserve(el);
        /* Drop the compositing hint once the transition has finished. */
        window.setTimeout(function () { el.classList.add('is-done'); }, 1400);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -7% 0px' });
    return io;
  }

  function isSkippedSection(section) {
    if (section.getAttribute('data-ek-reveal') === 'off') return true;
    if (section.getAttribute('data-ek-reveal') === 'self') return true;
    if (section.classList.contains('ek-no-reveal')) return true;
    if (SKIP_FIRST_SECTION && section === firstSection()) return true;
    /* Sections that run their own reveal sequence. */
    if (section.querySelector('[data-ek-timeline]')) return true;
    if (section.hasAttribute('data-ek-timeline')) return true;
    /* Sections driven by the theme's other animation system. */
    if (section.querySelector('.ek-anim')) return true;
    if (section.hasAttribute('data-ek-anim')) return true;

    var id = (section.id || '').toLowerCase();
    for (var i = 0; i < SKIP_SECTIONS.length; i++) {
      if (id.indexOf(SKIP_SECTIONS[i]) !== -1) return true;
    }
    /* Site chrome, whatever the section is called. */
    if (section.querySelector('header, footer, dialog')) return true;
    return false;
  }

  /* The first shopify-section in document order that is not site chrome. */
  var firstCache;
  function firstSection() {
    if (firstCache !== undefined) return firstCache;
    var all = document.querySelectorAll('[id^="shopify-section-"]');
    firstCache = null;
    for (var i = 0; i < all.length; i++) {
      var id = (all[i].id || '').toLowerCase();
      if (id.indexOf('header') !== -1 || id.indexOf('announcement') !== -1) continue;
      if (all[i].querySelector('header')) continue;
      firstCache = all[i];
      break;
    }
    return firstCache;
  }

  function isPinned(el) {
    var pos;
    try { pos = window.getComputedStyle(el).position; } catch (e) { return false; }
    return pos === 'fixed' || pos === 'sticky';
  }

  /* A background surface rather than content: an element that fills its parent
     and carries the paint — a colour, a gradient, an image. Fading one does not
     animate anything, it punches a hole in the page, and whatever sits behind
     shows through. That matters here because the hero is position:sticky, so a
     section fading in over it briefly reveals the pinned hero underneath.
     Absolutely positioned full-bleed layers (backgrounds, overlays, tints) are
     surfaces too, whatever they are painted with. */
  function isSurface(el) {
    var c;
    try { c = window.getComputedStyle(el); } catch (e) { return false; }
    var parent = el.parentElement;
    if (!parent) return false;
    var r = el.getBoundingClientRect(), p = parent.getBoundingClientRect();
    if (!p.width || !p.height) return false;
    if ((r.width * r.height) < (p.width * p.height) * 0.9) return false;

    if (c.position === 'absolute') return true;
    if (c.backgroundImage && c.backgroundImage !== 'none') return true;

    /* Any background colour that is not fully transparent. */
    var bg = c.backgroundColor || '';
    if (bg && bg !== 'transparent') {
      var m = bg.match(/rgba?\(([^)]+)\)/);
      if (!m) return true;
      var parts = m[1].split(',');
      var alpha = parts.length > 3 ? parseFloat(parts[3]) : 1;
      if (alpha > 0.01) return true;
    }
    return false;
  }

  function isRevealable(el) {
    if (!el || el.nodeType !== 1) return false;
    var tag = el.tagName;
    if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' ||
        tag === 'TEMPLATE' || tag === 'LINK') return false;
    if (el.classList.contains('ek-no-reveal')) return false;
    if (el.hasAttribute('data-ek-no-reveal')) return false;
    if (el.classList.contains('ek-rv')) return false;
    /* Owned by the theme's other animation system — driving opacity and
       transform from two places at once makes both look wrong. */
    if (el.classList.contains('ek-anim')) return false;
    if (isPinned(el)) return false;
    if (isSurface(el)) return false;
    /* Skip elements with no rendered box — hidden tabs, empty wrappers. */
    if (!el.offsetParent && window.getComputedStyle(el).position !== 'fixed') {
      if (el.offsetWidth === 0 && el.offsetHeight === 0) return false;
    }
    return true;
  }

  /* Walk down through wrappers that hold a single child, so the reveal lands
     on the row of cards rather than on the outermost <div>. */
  function contentNode(section) {
    var node = section, depth = 0;
    while (node && depth < MAX_DEPTH) {
      var kids = Array.prototype.filter.call(node.children, function (c) {
        var t = c.tagName;
        return t !== 'SCRIPT' && t !== 'STYLE' && t !== 'NOSCRIPT' && t !== 'TEMPLATE';
      });
      if (kids.length !== 1) return node;
      node = kids[0];
      depth++;
    }
    return node;
  }

  function targetsFor(section) {
    var node = contentNode(section);
    var kids = [];

    /* A surface is a wrapper, not content: step inside it and animate what it
       holds. A surface with nothing inside is pure decoration — a gradient
       band, a tint — and animating it is never right. */
    Array.prototype.forEach.call(node.children, function (child) {
      if (isRevealable(child)) { kids.push(child); return; }
      if (!isSurface(child) || !child.children.length) return;
      Array.prototype.forEach.call(contentNode(child).children, function (grandchild) {
        if (isRevealable(grandchild)) kids.push(grandchild);
      });
    });

    if (!kids.length) return isRevealable(node) ? [node] : [];
    if (kids.length > MAX_SIBLINGS) return isRevealable(node) ? [node] : [];
    return kids;
  }

  /* Hand-placed .ek-reveal markup keeps its existing look — no stagger, no
     auto-targeting on top of it. It is observed here as well as by
     ek-custom.js, because that file scans once on DOMContentLoaded and so
     misses sections the theme editor loads later. Whichever observer adds
     .is-in first wins; the second is a no-op. */
  function bindLegacy(section) {
    var legacy = section.querySelectorAll('.ek-reveal');
    if (!legacy.length) return false;
    Array.prototype.forEach.call(legacy, function (el) {
      if (el.dataset.ekRvBound) return;
      el.dataset.ekRvBound = '1';
      if (reduce || !('IntersectionObserver' in window)) el.classList.add('is-in');
      else observer().observe(el);
    });
    return true;
  }

  function apply(section) {
    if (!section || section.dataset.ekRevealBound) return;
    section.dataset.ekRevealBound = '1';
    if (isSkippedSection(section)) return;

    if (bindLegacy(section)) return;

    var targets = targetsFor(section);
    if (!targets.length) return;

    targets.forEach(function (el, i) {
      el.classList.add('ek-rv');
      if (isPinned(el)) {
        el.setAttribute('data-ek-rv-fixed', '');
        return;
      }
      var steps = i > MAX_STAGGER_STEPS ? MAX_STAGGER_STEPS : i;
      if (steps) el.style.setProperty('--ek-rv-delay', (steps * getStagger()) + 'ms');

      if (reduce || !('IntersectionObserver' in window)) el.classList.add('is-in');
      else observer().observe(el);
    });
  }

  var stagger = null;
  function getStagger() {
    if (stagger !== null) return stagger;
    var raw = window.getComputedStyle(document.documentElement)
                    .getPropertyValue('--ek-rv-stagger');
    stagger = parseInt(raw, 10);
    if (isNaN(stagger)) stagger = 90;
    return stagger;
  }

  function scan(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll('[id^="shopify-section-"]');
    Array.prototype.forEach.call(sections, apply);

    /* Themes that render content outside section wrappers (templates with raw
       markup, app blocks) still get the main content revealed. */
    if (!root && !sections.length) {
      var main = document.querySelector('main, #MainContent, [role="main"]');
      if (main) apply(main);
    }
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () { scan(); });

  /* Theme editor: new and re-rendered sections need binding too, or they sit
     invisible while the merchant is editing them. */
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target;
    if (section && section.dataset) delete section.dataset.ekRevealBound;
    apply(section);
  });

  /* Anything injected later — quick view, infinite scroll, app embeds. */
  if ('MutationObserver' in window) {
    var mo = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.id && n.id.indexOf('shopify-section-') === 0) apply(n);
          else if (n.querySelector) scan(n);
        }
      }
    });
    ready(function () {
      mo.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
/* ============================================================================
   HERO SINK GUARD
   ----------------------------------------------------------------------------
   The hero section is position:sticky with no constraining container, so it is
   not pinned for its own height — it stays pinned behind every section for the
   whole length of the page. At 6000px down it is still sitting at top:0. Any
   section above it that is transparent, or momentarily transparent while it
   fades in, lets the pinned hero show through.

   Hiding it once it has been scrolled past removes that possibility. Nothing is
   lost visually: at that point it is covered by opaque sections anyway. It is
   restored the moment you scroll back up into its range.

   visibility rather than opacity — an element at opacity 0 is still painted and
   composited, visibility:hidden is not, and the switch happens where nobody can
   see it so there is nothing to fade.
   ============================================================================ */
(function () {
  if (window.__ekHeroSink) return;
  window.__ekHeroSink = true;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var hero = document.querySelector('.ek-hero');
    if (!hero) return;
    var section = hero.closest('[id^="shopify-section-"]');
    if (!section) return;
    /* Only guard a hero that is actually pinned. If the sticky is ever removed
       from the theme, this does nothing. */
    try {
      if (window.getComputedStyle(section).position !== 'sticky') return;
    } catch (e) { return; }

    function docTop(el) { var t = 0; while (el) { t += el.offsetTop; el = el.offsetParent; } return t; }

    var base = docTop(section), past = null, ticking = false;

    function update() {
      ticking = false;
      var now = (base + hero.offsetHeight) - window.pageYOffset <= 0;
      if (now === past) return;          /* only touch the DOM on a change */
      past = now;
      section.style.visibility = now ? 'hidden' : '';
    }
    function onScroll() { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } }
    function onResize() { base = docTop(section); past = null; update(); }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    document.addEventListener('shopify:section:load', onResize);
    update();
  });
})();